// Vídeo da ligação de WhatsApp no navegador do atendente (Chrome/Edge).
//
// Formato do motor Go (o mesmo do exemplo web do meowcaller, que já conversa
// com o WhatsApp): H.264 baseline ("avc1.42E01F") em Annex-B, um quadro por
// mensagem binária no WebSocket, 15 quadros/s, no máximo 1280x720 (nível 3.1).
// Do motor também chegam avisos em texto: {"keyframe":true} quando o WhatsApp
// pede um quadro-chave e {"orientation":n} com a rotação do celular do cliente.

const CODEC = "avc1.42E01F";
const FPS = 15;
const MAX_W = 1280;
const MAX_H = 720;

export function callVideoSupported() {
  return Boolean(
    window.isSecureContext &&
      window.VideoEncoder &&
      window.VideoDecoder &&
      window.MediaStreamTrackProcessor &&
      navigator.mediaDevices?.getUserMedia,
  );
}

export function describeCameraError(err) {
  if (err?.name === "NotFoundError") return "nenhuma câmera encontrada neste computador";
  if (err?.name === "NotAllowedError") return "permissão da câmera negada (cadeado na barra de endereço)";
  if (err?.name === "NotReadableError") return "a câmera está em uso por outro programa";
  return err?.message || "erro desconhecido";
}

// Tamanho permitido pelo nível 3.1 do H.264, mantendo a proporção e par.
function fitSize(width, height) {
  const scale = Math.min(1, MAX_W / width, MAX_H / height);
  return {
    width: Math.max(2, Math.floor((width * scale) / 2) * 2),
    height: Math.max(2, Math.floor((height * scale) / 2) * 2),
  };
}

// Quadro-chave (IDR=5) ou parâmetros (SPS=7): o decodificador só pode começar daí.
function isKeyAccessUnit(data) {
  for (let i = 0; i + 4 < data.length; i++) {
    let p = -1;
    if (data[i] === 0 && data[i + 1] === 0 && data[i + 2] === 1) p = i + 3;
    else if (data[i] === 0 && data[i + 1] === 0 && data[i + 2] === 0 && data[i + 3] === 1) p = i + 4;
    if (p >= 0) {
      const type = data[p] & 31;
      if (type === 5 || type === 7) return true;
    }
  }
  return false;
}

export class WhatsAppCallVideo {
  constructor() {
    this.ws = null;
    this.stream = null;
    this.encoder = null;
    this.reader = null;
    this.decoder = null;
    this.decodeStarted = false;
    this.forceKeyframe = false;
    this.orientation = 0;
    this.canvas = null;
    this.localEl = null;
    this.onRemoteFrame = null; // avisa a tela que chegou vídeo do cliente
  }

  get cameraOn() {
    return Boolean(this.stream);
  }

  // A tela entrega onde desenhar o vídeo do cliente e onde mostrar a própria câmera.
  attach({ canvas, local }) {
    this.canvas = canvas || null;
    this.localEl = local || null;
    if (this.localEl) this.localEl.srcObject = this.stream;
  }

  connect(url, protocols) {
    try { this.ws?.close(); } catch {}
    const ws = new WebSocket(url, protocols);
    ws.binaryType = "arraybuffer";
    ws.onmessage = (e) => {
      if (typeof e.data === "string") {
        try {
          const msg = JSON.parse(e.data);
          if (msg.keyframe) this.forceKeyframe = true;
          if (Number.isInteger(msg.orientation)) this.orientation = msg.orientation;
        } catch {}
        return;
      }
      this.decode(new Uint8Array(e.data));
    };
    this.ws = ws;
    this.forceKeyframe = true;
  }

  async startCamera() {
    if (this.stream) return;
    this.stream = await navigator.mediaDevices.getUserMedia({
      video: {
        width: { ideal: MAX_W, max: MAX_W },
        height: { ideal: MAX_H, max: MAX_H },
        frameRate: { ideal: FPS, max: FPS },
      },
    });
    if (this.localEl) this.localEl.srcObject = this.stream;

    this.encoder = new window.VideoEncoder({
      output: (chunk) => {
        if (this.ws?.readyState !== WebSocket.OPEN) return;
        const data = new Uint8Array(chunk.byteLength);
        chunk.copyTo(data);
        this.ws.send(data);
      },
      error: () => this.stopCamera(),
    });
    const track = this.stream.getVideoTracks()[0];
    this.reader = new window.MediaStreamTrackProcessor({ track }).readable.getReader();
    this.forceKeyframe = true;
    void this.pump(this.reader, this.encoder);
  }

  async pump(reader, encoder) {
    let frames = 0;
    let width = 0;
    let height = 0;
    try {
      for (;;) {
        const { value: frame, done } = await reader.read();
        if (done) break;
        const size = fitSize(frame.displayWidth, frame.displayHeight);
        if (size.width !== width || size.height !== height) {
          ({ width, height } = size);
          encoder.configure({
            codec: CODEC,
            avc: { format: "annexb" },
            width,
            height,
            framerate: FPS,
            bitrate: Math.max(250000, Math.min(1500000, width * height * 2)),
            latencyMode: "realtime",
          });
          this.forceKeyframe = true;
        }
        // Fila cheia = rede ou CPU atrasados: pula o quadro em vez de acumular atraso.
        if (encoder.state === "configured" && encoder.encodeQueueSize < 2) {
          const keyFrame = this.forceKeyframe || frames % FPS === 0;
          this.forceKeyframe = false;
          encoder.encode(frame, { keyFrame });
          frames++;
        }
        frame.close();
      }
    } catch {
      if (reader === this.reader) await this.stopCamera();
    }
  }

  async stopCamera() {
    const { reader, encoder, stream } = this;
    this.reader = this.encoder = this.stream = null;
    if (reader) await reader.cancel().catch(() => {});
    if (encoder && encoder.state !== "closed") encoder.close();
    stream?.getTracks().forEach((t) => t.stop());
    if (this.localEl) this.localEl.srcObject = null;
  }

  getDecoder() {
    if (this.decoder && this.decoder.state !== "closed") return this.decoder;
    this.decoder = new window.VideoDecoder({
      output: (frame) => {
        this.draw(frame);
        frame.close();
      },
      error: () => this.resetDecoder(),
    });
    this.decoder.configure({ codec: CODEC, optimizeForLatency: true });
    return this.decoder;
  }

  resetDecoder() {
    this.decodeStarted = false;
    if (this.decoder && this.decoder.state !== "closed") this.decoder.close();
    this.decoder = null;
  }

  decode(accessUnit) {
    const key = isKeyAccessUnit(accessUnit);
    if (!this.decodeStarted && !key) return;
    this.decodeStarted = true;
    try {
      this.getDecoder().decode(
        new window.EncodedVideoChunk({
          type: key ? "key" : "delta",
          timestamp: performance.now() * 1000,
          data: accessUnit,
        }),
      );
    } catch {
      this.resetDecoder();
    }
  }

  // Desenha o quadro do cliente girado conforme a orientação do celular dele.
  draw(frame) {
    const canvas = this.canvas;
    if (!canvas) return;
    const w = frame.displayWidth;
    const h = frame.displayHeight;
    const q = ((this.orientation % 4) + 4) % 4;
    const portrait = q % 2 === 1;
    canvas.width = portrait ? h : w;
    canvas.height = portrait ? w : h;
    const g = canvas.getContext("2d");
    g.save();
    if (q === 1) {
      g.translate(canvas.width, 0);
      g.rotate(Math.PI / 2);
    } else if (q === 2) {
      g.translate(canvas.width, canvas.height);
      g.rotate(Math.PI);
    } else if (q === 3) {
      g.translate(0, canvas.height);
      g.rotate(-Math.PI / 2);
    }
    g.drawImage(frame, 0, 0, w, h);
    g.restore();
    this.onRemoteFrame?.();
  }

  stop() {
    void this.stopCamera();
    try { this.ws?.close(); } catch {}
    this.ws = null;
    this.resetDecoder();
    if (this.canvas) this.canvas.getContext("2d")?.clearRect(0, 0, this.canvas.width, this.canvas.height);
  }
}
